import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { Brand } from '~/types/user';

import { useFavoriteBrand } from '~/api/user/useFavoriteBrand';
import { AlertDialog, Button, Menu } from '~/components';
import { optionsMenuTriggerStyle } from '~/components/Table/TableRow.css';
import { AnchorTargets } from '~/constants/url';
import { useDialog } from '~/hooks/useDialog';
import { routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import type { DialogHandles } from '../BrandsTable.hooks';

import { EditBrandModal } from '../CreateEditBrandModal/EditBrandModal';

type Props = {
  brand: Brand;
  dialogHandles: DialogHandles;
};

export const BrandOptionsCell = ({ brand, dialogHandles }: Props) => {
  const mutation = useFavoriteBrand(brand.id);

  const editBrandDialog = useDialog();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);

  const onUpdateFavoriteBrand = (isFavorite: boolean) => {
    mutation.mutate(isFavorite, {
      onSuccess: () => toast.success({ description: t('settings:alerts.brands.successFavorite') }),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  return (
    <div>
      <Menu.Root>
        <Menu.Trigger
          render={
            <Button
              aria-label={t('common:actions.openMenu')}
              className={optionsMenuTriggerStyle}
              icon="MoreVert"
              loading={mutation.isPending}
            />
          }
        />

        <Menu.List>
          {brand.isPrimary ? (
            <Menu.Item
              icon={brand.logo ? 'Edit' : 'Add'}
              onClick={() => navigate({ pathname: routes.settingsInvoice, hash: AnchorTargets.SETTINGS_INVOICE_THEME })}
            >
              {brand.logo ? t('settings:profile.brands.logoEdit') : t('settings:profile.brands.logoAdd')}
            </Menu.Item>
          ) : (
            <>
              <Menu.Item icon="Edit" onClick={() => editBrandDialog.open()}>
                {t('common:actions.edit')}
              </Menu.Item>

              <Menu.Item icon="Star" onClick={() => onUpdateFavoriteBrand(!brand.isFavorite)}>
                {brand.isFavorite
                  ? t('settings:profile.brands.favorite.removeFavorite')
                  : t('settings:profile.brands.favorite.markAsFavorite')}
              </Menu.Item>

              <AlertDialog.Trigger
                handle={dialogHandles.deleteBrandHandle}
                nativeButton={false}
                payload={brand}
                render={<Menu.Item icon="Trash">{t('common:actions.delete')}</Menu.Item>}
              />
            </>
          )}
        </Menu.List>
      </Menu.Root>

      {editBrandDialog.isOpen && <EditBrandModal brand={brand} onClose={editBrandDialog.close} />}
    </div>
  );
};
